import { App } from '@capacitor/app'
import { PushNotifications } from '@capacitor/push-notifications'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { toast } from 'sonner'

import { getPushToken, onPushAction } from '@/features/notification/push/pushState'
import { useCallingMe, callingKeys } from '@/hooks/useCalling'
import { getCall, registerDevice } from '@/services/api/calling.service'
import { useAuthStore } from '@/stores/auth.store'
import { useCallStore } from '@/stores/call.store'
import { useWorkspaceStore } from '@/stores/workspace.store'
import { logger } from '@/utils/logger'
import { isNativeApp } from '@/utils/platform'

import { syncCallLog } from './callLogSync'
import { dialOnThisPhone } from './simCallFlow'
import { simStorage } from './simStorage'

async function register(): Promise<void> {
  const info = await App.getInfo()
  const device = await registerDevice({
    installationId: await simStorage.installationId(),
    platform: 'ANDROID',
    deviceLabel: info.name,
    appVersion: info.version,
    fcmToken: getPushToken() ?? undefined,
    simAccountId: (await simStorage.workSim()) ?? undefined,
  })
  await simStorage.setDeviceId(device.id)
}

/**
 * Android app only: registers this phone with the server on start, keeps the call log in sync when
 * the app comes back to the foreground, and dials "tap to call" pushes sent from the desk. The push
 * token, permission and listeners belong to `usePushRegistration`; this adds the `calls` channel and
 * the `call.dial` handler on top.
 */
export function useNativeCalling() {
  const accessToken = useAuthStore((s) => s.accessToken)
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspaceId)
  const { data: me } = useCallingMe()
  const queryClient = useQueryClient()
  const callingEnabled = !!me?.featureEnabled && !!me?.canMakeCalls
  const region = me?.defaultRegion ?? 'IN'

  useEffect(() => {
    if (!isNativeApp() || !accessToken || !callingEnabled) return
    let cancelled = false

    const registerAndSync = async () => {
      try {
        await register()
        if (cancelled) return
        void queryClient.invalidateQueries({ queryKey: callingKeys.me(workspaceId) })
        await syncCallLog(region)
      } catch (error) {
        logger.warn('Phone registration or call-log sync failed', error)
      }
    }

    const setUpCallPush = async () => {
      try {
        // The server sends "tap to call" on this channel; high importance so it shows as a heads-up.
        await PushNotifications.createChannel({
          id: 'calls',
          name: 'Calls',
          description: 'Calls started from the desk, to place on this phone',
          importance: 5,
          visibility: 1,
          vibration: true,
        })
      } catch (error) {
        logger.warn('Could not create the calls notification channel', error)
      }
    }

    const stopDialHandler = onPushAction('call.dial', (data) => {
      if (!data.callId) return
      void (async () => {
        try {
          const call = await getCall(data.callId)
          if (call.status !== 'INITIATED') {
            toast.info('This call was already handled')
            return
          }
          useCallStore.getState().addCall(call.id)
          await dialOnThisPhone(call)
        } catch (error) {
          toast.error(error instanceof Error ? error.message : 'Could not start the call')
        }
      })()
    })

    void registerAndSync()
    void setUpCallPush()
    const appState = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) void syncCallLog(region).catch((error) => logger.warn('Call-log sync failed', error))
    })

    return () => {
      cancelled = true
      void appState.then((handle) => handle.remove())
      stopDialHandler()
    }
  }, [accessToken, workspaceId, callingEnabled, region, queryClient])
}
